import { useId } from 'react';

import type { ApiProduct } from '../../../../../models/ecommerce/product/ApiProduct';
import { Helpers } from '../../../../../utility/helpers';
import { type RecipeCartState } from '../../../../recipe/hooks/useRecipeCart';
import type { RecipeItemState } from '../../../../recipe/models';
import { ProductListing } from '../../ProductListing';
import { ProductListingGroup } from '../../ProductListing/ProductListing';
import styles from './BuyableRecipe.module.scss';
import BuyableRecipeCartSectionItem from './BuyableRecipeCartSectionItem';

const BuyableRecipeCartSections = (props: {
    cart: RecipeCartState;
    cartProducts: ApiProduct[];
    onItemRemove: (item: RecipeItemState) => void;
    onItemQuantityChange: (item: RecipeItemState, newQuantity: number) => void;
}) => {
    const hasSharedSection = props.cart.sections.some((s) => s.type === 'shared');
    const goodToHaveHeaderId = useId();

    return (
        <>
            {props.cart.sections.map((section) => {
                const sectionItems = props.cart?.items.filter((item) =>
                    section.substituteIdentifiers.includes(item.substituteIdentifier),
                );

                if (section.type === 'recipe') {
                    return (
                        <ProductListing ignoreBottomBorder={!hasSharedSection} key={section.id}>
                            {sectionItems.map((sectionItem) => (
                                <li key={sectionItem.id}>
                                    <BuyableRecipeCartSectionItem
                                        item={sectionItem}
                                        cartProducts={props.cartProducts}
                                        section={section}
                                        onQuantityChange={props.onItemQuantityChange}
                                        onItemRemove={props.onItemRemove}
                                    />
                                </li>
                            ))}
                        </ProductListing>
                    );
                }

                if (section.type === 'shared') {
                    return (
                        <ProductListingGroup
                            groupName="Varor som delas mellan recept"
                            headingAs="h2"
                            key={section.id}
                        >
                            {sectionItems.map((sectionItem) => (
                                <li key={sectionItem.id}>
                                    <BuyableRecipeCartSectionItem
                                        item={sectionItem}
                                        cartProducts={props.cartProducts}
                                        section={section}
                                        onQuantityChange={props.onItemQuantityChange}
                                        onItemRemove={props.onItemRemove}
                                    />
                                </li>
                            ))}
                        </ProductListingGroup>
                    );
                }

                if (section.type === 'common') {
                    return (
                        <div className={styles.CommonSection} key={section.id}>
                            <h2 id={goodToHaveHeaderId} className={styles.CommonSectionHeader}>
                                Bra att ha hemma
                            </h2>
                            <ProductListing
                                className={styles.CommonProductList}
                                ignoreBottomBorder
                                aria-labelledby={goodToHaveHeaderId}
                            >
                                {sectionItems.map((sectionItem) => (
                                    <li key={sectionItem.id}>
                                        <BuyableRecipeCartSectionItem
                                            className={styles.Item}
                                            item={sectionItem}
                                            cartProducts={props.cartProducts}
                                            section={section}
                                            onQuantityChange={props.onItemQuantityChange}
                                            onItemRemove={props.onItemRemove}
                                        />
                                    </li>
                                ))}
                            </ProductListing>
                        </div>
                    );
                }

                return Helpers.exhaustiveCheck(section);
            })}
        </>
    );
};

export default BuyableRecipeCartSections;
